import { AfterViewInit, Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import * as L from 'leaflet';
import { IGustParticle, IWindParticle } from 'src/app/share/interfaces';

@Component({
  selector: 'app-map',
  standalone: false,
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.css']
})
export class MapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() latitude: number | null = null;
  @Input() longitude: number | null = null;
  @Input() placeName: string | null = null;
  @Input() windDirection: number | null = null;
  @Input() windSpeed: number | null = null;
  @Input() windGustSpeed: number | null = null;

  // Inputs for Thermal Index
  @Input() thermalIndex700hPa: number | null = null;
  @Input() thermalIndex850hPa: number | null = null;

  private map: L.Map | undefined;
  private primaryMarker: L.Marker | undefined;
  private animationFrameId: number = 0;

  // Wind Particles
  private windParticles: IWindParticle[] = [];
  private readonly NUM_PARTICLES = 50;
  private readonly MAX_TRAIL_LENGTH = 10;
  private readonly PARTICLE_LIFE_MIN = 200;
  private readonly PARTICLE_LIFE_MAX = 500;
  private readonly PARTICLE_SPEED_SCALE = 0.000005;
  private readonly MAX_DISTANCE_FROM_CENTER_KM = 5;

  // NEW: Gust Particles Properties
  private gustParticles: IGustParticle[] = [];
  private readonly NUM_GUST_PARTICLES = 15; // Max number of simultaneous gust particles
  private readonly GUST_LIFE_MIN = 80;   // Gusts are shorter-lived
  private readonly GUST_LIFE_MAX = 200;
  private readonly GUST_SPEED_SCALE = 0.00001; // Gusts appear faster
  private readonly GUST_COLOR = 'red';
  private readonly GUST_RADIUS = 3; // Small circle marker for gusts
  private readonly GUST_SPAWN_CHANCE_PER_FRAME = 0.02; // Probability of spawning a gust on each frame


  // NEW: Properties for managing multiple thermal index circles
  private thermalIndexCircles850hPa: L.Circle[] = [];
  private thermalIndexCircles700hPa: L.Circle[] = [];

  // NEW: Constants for multi-circle radii
  private readonly MAX_THERMAL_CIRCLES = 8; // Cap the number of circles to prevent performance issues
  private readonly BASE_RADIUS_850_METERS = 100; // Smallest radius for the innermost 850hPa circle
  private readonly RADIUS_INCREMENT_850_METERS = 60; // How much each successive 850hPa circle expands

  private readonly BASE_RADIUS_700_METERS = 250; // Smallest radius for the innermost 700hPa circle
  private readonly RADIUS_INCREMENT_700_METERS = 100; // How much each successive 700hPa circle expands


  private customIcon = L.icon({

    iconUrl: 'assets/images/custom-marker2.png',      // Your main custom marker image


    iconSize: [60, 50], // Size of the icon in pixels
    iconAnchor: [23, 46], // Point of the icon which will correspond to marker's location
    popupAnchor: [1, -34], // Point from which popups will "open", relative to the iconAnchor
    tooltipAnchor: [16, -28], // Point from which tooltips will "open", relative to the iconAnchor
  });

  constructor() { }

  ngAfterViewInit(): void {
    this.initMap();
    if (this.latitude !== null && this.longitude !== null) {
      this.updateMapLocation(this.latitude, this.longitude);
      this.startAnimationLoop();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    let shouldRestartAnimation = false;

    if (changes['latitude'] || changes['longitude']) {
      if (this.map && this.latitude !== null && this.longitude !== null) {
        this.updateMapLocation(this.latitude, this.longitude);
        shouldRestartAnimation = true;
      }
    }

    // NEW: Include windGustSpeed in the change detection for animation restart
    if ((changes['windDirection'] || changes['windSpeed'] || changes['windGustSpeed']) && this.map) {
      shouldRestartAnimation = true;
    }
    // Update circles if thermal index data changes
    if ((changes['thermalIndex700hPa'] || changes['thermalIndex850hPa'])) {
      this.updateThermalIndexCircles(); // Re-draw circles based on new power
      shouldRestartAnimation = true; // Ensure animation loop is running for pulsation
    }

    if (shouldRestartAnimation && this.map) {
      this.stopAnimationLoop();
      this.startAnimationLoop();
    }
  }

  ngOnDestroy(): void {
    this.stopAnimationLoop();
    this.cleanupParticles();
    this.cleanupGustParticles();
    this.cleanupThermalIndexCircles(this.thermalIndexCircles850hPa); // Clean up both sets of circles
    this.cleanupThermalIndexCircles(this.thermalIndexCircles700hPa);
    if (this.map) {
      this.map.remove();
      this.map = undefined;
    }
  }

  private initMap(): void {
    if (this.map) return;

    const mapContainer = document.getElementById('map');
    if (!mapContainer) {
      console.warn('Map container #map not found. Retrying initMap...');
      setTimeout(() => this.initMap(), 100);
      return;
    }

    this.map = L.map('map', {
      center: [0, 0], // Default center, will be updated
      zoom: 15, // Default zoom level
      zoomControl: true, // removes + / - buttons
      scrollWheelZoom: false,
      doubleClickZoom: true,
      boxZoom: false,
      touchZoom: false,
      dragging: true // optional if you also want to fix the position
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
    }).addTo(this.map);
  }

  private updateMapLocation(lat: number, lng: number): void {
    if (!this.map) return;

    const newLatLng = L.latLng(lat, lng);
    this.map.setView(newLatLng, 13);

    if (this.primaryMarker) {
      this.map.removeLayer(this.primaryMarker);
    }
    this.primaryMarker = L.marker(newLatLng, { icon: this.customIcon }).addTo(this.map);
    if (this.placeName) {
      this.primaryMarker.bindPopup(`<b>${this.placeName}</b>`).openPopup();
    }
    // Re-draw thermal index circles when location changes
    this.updateThermalIndexCircles();
  }

  private degToRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  private startAnimationLoop(): void {
    if (!this.map || this.latitude === null || this.longitude === null) {
      return;
    }
    this.cleanupParticles();
    this.cleanupGustParticles();
    this.cleanupThermalIndexCircles(this.thermalIndexCircles850hPa);
    this.cleanupThermalIndexCircles(this.thermalIndexCircles700hPa);


    if (this.windDirection !== null && this.windSpeed !== null) {
      for (let i = 0; i < this.NUM_PARTICLES; i++) {
        this.createWindParticle();
      }
    }
    this.updateThermalIndexCircles(); // Initialize/update circles

    this.animationFrameId = requestAnimationFrame(this.animateMapEffects.bind(this));
  }

  private stopAnimationLoop(): void {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  private cleanupParticles(): void {
    this.windParticles.forEach(p => {
      this.map?.removeLayer(p.marker);
      this.map?.removeLayer(p.polyline);
    });
    this.windParticles = [];
  }

  private createWindParticle(): void {
    if (this.latitude === null || this.longitude === null || !this.map) return;

    const center = L.latLng(this.latitude, this.longitude);
    const spawnRadius = 0.05; // ~50 meters radius for initial spawn around center

    const randomAngle = Math.random() * 2 * Math.PI;
    const randomDistance = Math.random() * spawnRadius;

    const initialLat = center.lat + randomDistance * Math.cos(randomAngle);
    const initialLng = center.lng + randomDistance * Math.sin(randomAngle) / Math.cos(this.degToRad(center.lat));

    const markerIcon = L.divIcon({
      className: 'wind-dot',
      iconSize: [6, 6]
    });

    const particle: IWindParticle = {
      id: Math.random().toString(36).substring(2, 9),
      currentLat: initialLat,
      currentLng: initialLng,
      marker: L.marker([initialLat, initialLng], { icon: markerIcon }).addTo(this.map),
      polyline: L.polyline([], { color: 'rgba(0, 150, 255, 0.7)', weight: 1 }).addTo(this.map),
      trailPoints: [],
      life: Math.floor(Math.random() * (this.PARTICLE_LIFE_MAX - this.PARTICLE_LIFE_MIN)) + this.PARTICLE_LIFE_MIN,
      maxLife: Math.floor(Math.random() * (this.PARTICLE_LIFE_MAX - this.PARTICLE_LIFE_MIN)) + this.PARTICLE_LIFE_MIN
    };
    this.windParticles.push(particle);
  }

  private createGustParticle(): void {
    if (this.latitude === null || this.longitude === null || !this.map ||
      this.windGustSpeed === null || this.windDirection === null) {
      return;
    }

    const bounds = this.map.getBounds();
    const minLat = bounds.getSouthWest().lat;
    const maxLat = bounds.getNorthEast().lat;
    const minLng = bounds.getSouthWest().lng;
    const maxLng = bounds.getNorthEast().lng;

    // Random starting position within current map bounds
    // We want them to spawn *anywhere* visible on the map, not just near the center marker
    const initialLat = minLat + (Math.random() * (maxLat - minLat));
    const initialLng = minLng + (Math.random() * (maxLng - minLng));

    // Convert gust speed from km/h to approximate units for animation scale
    // This value needs to be tuned based on your GUST_SPEED_SCALE
    const gustSpeed = (this.windGustSpeed || 0) * 1000 / 3600; // km/h to m/s

    // Use current wind direction, maybe add a small random variance for "gustiness"
    const gustDirection = (this.windDirection || 0) + (Math.random() * 30 - 15); // +/- 15 degrees randomness

    // NEW: Create a custom divIcon for the red dot (this replaces L.circleMarker)
    const gustDotIcon = L.divIcon({
      className: 'gust-dot', // This CSS class (from map.component.css) defines the dot's appearance
      iconSize: [this.GUST_RADIUS * 2, this.GUST_RADIUS * 2] // Size based on radius
    });

    const gust: IGustParticle = {
      id: Math.random().toString(36).substring(2, 9),
      currentLat: initialLat,
      currentLng: initialLng,
      marker: L.marker([initialLat, initialLng], { icon: gustDotIcon }).addTo(this.map), // <-- IMPORTANT: Use L.marker with custom icon
      polyline: L.polyline([], { color: 'red', weight: 1, opacity: 0.7 }).addTo(this.map), // <-- IMPORTANT: Initialize polyline
      trailPoints: [], // <-- IMPORTANT: Initialize trailPoints array
      life: Math.floor(Math.random() * (this.GUST_LIFE_MAX - this.GUST_LIFE_MIN)) + this.GUST_LIFE_MIN,
      maxLife: Math.floor(Math.random() * (this.GUST_LIFE_MAX - this.GUST_LIFE_MIN)) + this.GUST_LIFE_MIN,
      speed: gustSpeed,
      direction: gustDirection
    };
    this.gustParticles.push(gust);
  }

  // NEW: Method to cleanup gust particles
  private cleanupGustParticles(): void {
    this.gustParticles.forEach(p => {
      this.map?.removeLayer(p.marker);
      this.map?.removeLayer(p.polyline); // ADD this line to remove polyline
    });
    this.gustParticles = [];
  }

  // --- Thermal Index Logic: Multiple Concentric Circles ---

  private getThermalIndexColor(index: number, elevation: number): string {
    if (index === null || isNaN(index)) return 'rgba(128, 128, 128, 0.5)';
    if (elevation === 850) {
      if (index <= -6) {
        return 'rgba(255, 0, 0, 0.5)';
      } else if (index <= -4) {
        return 'rgba(248, 162, 101, 0.5)';
      } else if (index < 0) {
        return 'rgba(79, 226, 50, 0.64)';
      }
    } else if (elevation === 700) {
      if (index <= -6) {
        return 'rgba(255, 0, 102, 0.74)';
      } else if (index <= -4) {
        return 'rgba(214, 101, 248, 0.5)';
      } else if (index < 0) {
        return 'rgba(0, 102, 255, 0.4)';
      }
    }
  }

  private updateThermalIndexCircles(): void {
    if (!this.map || this.latitude === null || this.longitude === null) {
      return;
    }
    const center = L.latLng(this.latitude, this.longitude);

    // --- Handle 850hPa Circles ---
    this.cleanupThermalIndexCircles(this.thermalIndexCircles850hPa); // Remove all existing 850hPa circles

    // Only draw circles if index is <= -1 (as per previous requirement)
    if (this.thermalIndex850hPa !== null && !isNaN(this.thermalIndex850hPa) && this.thermalIndex850hPa <= -0.5) {
      const numCircles850 = Math.min(this.MAX_THERMAL_CIRCLES, Math.min(this.MAX_THERMAL_CIRCLES, Math.max(1, Math.round(Math.abs(this.thermalIndex850hPa))))); // Number of circles (e.g., -5 -> 5 circles)
      const color850 = this.getThermalIndexColor(this.thermalIndex850hPa, 850);

      for (let i = 0; i < numCircles850; i++) {
        const radius = this.BASE_RADIUS_850_METERS + (i * this.RADIUS_INCREMENT_850_METERS);
        const circle = L.circle(center, {
          radius: radius,
          color: color850,
          fillColor: color850,
          fillOpacity: 0.3, // Base opacity for pulsation
          weight: 2,
          interactive: false
        }).addTo(this.map);
        this.thermalIndexCircles850hPa.push(circle);
      }
    }

    // --- Handle 700hPa Circles ---
    this.cleanupThermalIndexCircles(this.thermalIndexCircles700hPa); // Remove all existing 700hPa circles

    // Only draw circles if index is <= -1
    if (this.thermalIndex700hPa !== null && !isNaN(this.thermalIndex700hPa) && this.thermalIndex700hPa <= -0.5) {
      const numCircles700 = Math.min(this.MAX_THERMAL_CIRCLES, Math.min(this.MAX_THERMAL_CIRCLES, Math.max(1, Math.round(Math.abs(this.thermalIndex700hPa)))));
      const color700 = this.getThermalIndexColor(this.thermalIndex700hPa, 700);

      for (let i = 0; i < numCircles700; i++) {
        const radius = this.BASE_RADIUS_700_METERS + (i * this.RADIUS_INCREMENT_700_METERS);
        const circle = L.circle(center, {
          radius: radius,
          color: color700,
          fillColor: color700,
          fillOpacity: 0.3,
          weight: 2,
          interactive: false
        }).addTo(this.map);
        this.thermalIndexCircles700hPa.push(circle);
      }
    }
  }

  // Modified cleanup function to accept an array of circles
  private cleanupThermalIndexCircles(circlesArray: L.Circle[]): void {
    circlesArray.forEach(circle => {
      this.map?.removeLayer(circle);
    });
    circlesArray.length = 0; // Clear the array
  }


  // --- Combined Animation Loop ---
  private animateMapEffects(): void {
    if (!this.map || this.latitude === null || this.longitude === null) {
      this.stopAnimationLoop();
      return;
    }

    // 1. Wind Particle Animation (no changes here, already correct)
    if (this.windDirection !== null && this.windSpeed !== null) {
      const effectiveWindDirection = (this.windDirection + 180) % 360; // Wind blows FROM direction
      const windAngleRad = this.degToRad(90 - effectiveWindDirection);

      this.windParticles.forEach(p => {
        p.life--;

        const deltaLat = this.windSpeed! * Math.sin(windAngleRad) * this.PARTICLE_SPEED_SCALE;
        const deltaLng = this.windSpeed! * Math.cos(windAngleRad) * this.PARTICLE_SPEED_SCALE / Math.cos(this.degToRad(p.currentLat));

        p.currentLat += deltaLat;
        p.currentLng += deltaLng;

        const currentPoint = L.latLng(p.currentLat, p.currentLng);
        const centerPoint = L.latLng(this.latitude!, this.longitude!);

        // Check if particle is off-screen or life has expired
        if (p.life <= 0 || currentPoint.distanceTo(centerPoint) / 1000 > this.MAX_DISTANCE_FROM_CENTER_KM) {
          this.map?.removeLayer(p.marker);
          this.map?.removeLayer(p.polyline);
          // Instead of splicing, re-create particle in place or push to a new array
          // For simplicity, let's remove and create a new one to keep particle count constant
          // Find index and remove, then recreate at the end of the loop
          const index = this.windParticles.indexOf(p);
          if (index > -1) {
            this.windParticles.splice(index, 1);
            this.createWindParticle(); // Re-create a new particle to maintain constant count
          }
          return; // Skip remaining logic for this particle
        }

        p.marker.setLatLng([p.currentLat, p.currentLng]);

        // Update polyline trail
        p.trailPoints.push(L.latLng(p.currentLat, p.currentLng));
        if (p.trailPoints.length > this.MAX_TRAIL_LENGTH) {
          p.trailPoints.shift();
        }
        p.polyline.setLatLngs(p.trailPoints);

        // Optional: Fade out particles as they near end of life or edge
        const opacity = p.life / p.maxLife;
        p.marker.setOpacity(opacity);
        p.polyline.setStyle({ opacity: opacity });
      });
    }

    // NEW: 3. Gust Particle Animation (MODIFIED)
    if (this.windGustSpeed !== null && this.windDirection !== null) {
      // Remove expired gust particles
      this.gustParticles = this.gustParticles.filter(gust => {
        gust.life--;
        if (gust.life <= 0) {
          this.map?.removeLayer(gust.marker);
          this.map?.removeLayer(gust.polyline); // Ensure polyline is removed
          return false; // Remove from array
        }

        // Animate position
        const effectiveGustDirection = (gust.direction + 180) % 360; // Gust blows FROM direction
        const gustAngleRad = this.degToRad(90 - effectiveGustDirection);

        const deltaLat = gust.speed * Math.sin(gustAngleRad) * this.GUST_SPEED_SCALE;
        const deltaLng = gust.speed * Math.cos(gustAngleRad) * this.GUST_SPEED_SCALE / Math.cos(this.degToRad(gust.currentLat));

        gust.currentLat += deltaLat;
        gust.currentLng += deltaLng;
        gust.marker.setLatLng([gust.currentLat, gust.currentLng]); // Update marker position

        // IMPORTANT: Update polyline trail for gust particles
        gust.trailPoints.push(L.latLng(gust.currentLat, gust.currentLng));
        if (gust.trailPoints.length > this.MAX_TRAIL_LENGTH) { // Use MAX_TRAIL_LENGTH for gusts too
          gust.trailPoints.shift(); // Remove oldest point
        }
        gust.polyline.setLatLngs(gust.trailPoints); // Update polyline with new trail points

        // Fade out
        const opacity = gust.life / gust.maxLife;
        gust.marker.setOpacity(opacity); // Correct way to set opacity for L.Marker
        gust.polyline.setStyle({ opacity: opacity }); // Set polyline opacity

        return true; // Keep in array
      });

      // Spawn new gust particles randomly in time and place
      if (this.gustParticles.length < this.NUM_GUST_PARTICLES && Math.random() < this.GUST_SPAWN_CHANCE_PER_FRAME) {
        this.createGustParticle();
      }
    }


    // 2. Thermal Index Circle Pulsation (Opacity Animation for ALL circles)
    if (this.thermalIndexCircles850hPa.length > 0 || this.thermalIndexCircles700hPa.length > 0) {
      const pulseFactor = (Math.sin(performance.now() / 700) + 1) / 2;
      const minPulseOpacity = 0.2;
      const maxPulseOpacity = 0.4;
      const pulseOpacity = minPulseOpacity + (maxPulseOpacity - minPulseOpacity) * pulseFactor;

      // Apply pulsation to 850hPa circles
      this.thermalIndexCircles850hPa.forEach(circle => {
        circle.setStyle({ fillOpacity: pulseOpacity });
      });
      // Apply pulsation to 700hPa circles
      this.thermalIndexCircles700hPa.forEach(circle => {
        circle.setStyle({ fillOpacity: pulseOpacity });
      });
      // NOTE: Radius animation (growing from small to final size) is NOT implemented here for each circle.
      // The circles are drawn at their final size. The "animation" is in the number of circles and their pulsation.
    }

    this.animationFrameId = requestAnimationFrame(this.animateMapEffects.bind(this));
  }
}
